/**
 * MobileUserCard Component
 *
 * Card-based display for user data on mobile devices.
 * Replaces the DataTable view for better touch interaction.
 */

import React from 'react';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system/tokens';
import { AppRoutes } from '@consts/routes';
import type { User } from '@features/redux/apis/auth';

interface MobileUserCardProps {
    data: User;
    companies: Record<string, string>;
    resellers: Record<string, string>;
    locations: Record<string, string>;
}

const CardContainer: React.FC<{ children: React.ReactNode; to: string }> = ({ children, to }) => (
    <Box
        component={RouterLink}
        to={to}
        sx={{
            display: 'block',
            textDecoration: 'none',
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
            boxShadow: shadows.card,
            transition: 'all 150ms ease',
            '&:hover': {
                boxShadow: shadows.elevated,
                borderColor: colors.primary[200],
            },
            '&:active': {
                transform: 'scale(0.99)',
            },
        }}>
        {children}
    </Box>
);

const getEntityNames = (
    ids: string | undefined,
    nameMap: Record<string, string>
): string[] => {
    if (!ids) return [];
    return ids
        .split(',')
        .filter(Boolean)
        .map((id) => nameMap[id] || `#${id}`)
        .slice(0, 3); // Limit to 3 items for display
};

export const MobileUserCard: React.FC<MobileUserCardProps> = ({
    data,
    companies,
    resellers,
    locations,
}) => {
    const fullName = `${data.given_name || ''} ${data.family_name || ''}`.trim() || 'Unknown User';
    const email = data.email || '';

    // Admin users have '*' for all scopes
    const isAdmin =
        data['custom:Companies'] === '*' &&
        data['custom:Resellers'] === '*' &&
        data['custom:Locations'] === '*';

    const companyNames = getEntityNames(data['custom:Companies'], companies);
    const resellerNames = getEntityNames(data['custom:Resellers'], resellers);
    const locationNames = getEntityNames(data['custom:Locations'], locations);

    return (
        <CardContainer to={`${AppRoutes.ManageUser}?id=${data.sub}`}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <PersonIcon sx={{ color: colors.primary[500], fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.md,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                            lineHeight: 1.3,
                        }}>
                        {fullName}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            mt: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {email}
                    </Typography>
                </Box>
                {isAdmin && (
                    <Chip
                        size='small'
                        label='Admin'
                        sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            backgroundColor: colors.primary[100],
                            color: colors.primary[700],
                            fontWeight: 500,
                        }}
                    />
                )}
            </Box>

            {/* Organization Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {companyNames.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[400],
                                minWidth: 60,
                            }}>
                            Companies
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                            {companyNames.map((name, idx) => (
                                <Chip
                                    key={idx}
                                    size='small'
                                    label={name}
                                    sx={{
                                        height: 18,
                                        fontSize: '0.625rem',
                                        backgroundColor: colors.neutral[100],
                                        color: colors.neutral[700],
                                    }}
                                />
                            ))}
                            {(data['custom:Companies']?.split(',').length || 0) > 3 && (
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.xs,
                                        color: colors.neutral[500],
                                    }}>
                                    +{(data['custom:Companies']?.split(',').length || 0) - 3}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {resellerNames.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[400],
                                minWidth: 60,
                            }}>
                            Resellers
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                            {resellerNames.map((name, idx) => (
                                <Chip
                                    key={idx}
                                    size='small'
                                    label={name}
                                    sx={{
                                        height: 18,
                                        fontSize: '0.625rem',
                                        backgroundColor: colors.neutral[100],
                                        color: colors.neutral[700],
                                    }}
                                />
                            ))}
                            {(data['custom:Resellers']?.split(',').length || 0) > 3 && (
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.xs,
                                        color: colors.neutral[500],
                                    }}>
                                    +{(data['custom:Resellers']?.split(',').length || 0) - 3}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {locationNames.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[400],
                                minWidth: 60,
                            }}>
                            Locations
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                            {locationNames.map((name, idx) => (
                                <Chip
                                    key={idx}
                                    size='small'
                                    label={name}
                                    sx={{
                                        height: 18,
                                        fontSize: '0.625rem',
                                        backgroundColor: colors.neutral[100],
                                        color: colors.neutral[700],
                                    }}
                                />
                            ))}
                            {(data['custom:Locations']?.split(',').length || 0) > 3 && (
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.xs,
                                        color: colors.neutral[500],
                                    }}>
                                    +{(data['custom:Locations']?.split(',').length || 0) - 3}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </CardContainer>
    );
};

// Loading skeleton for mobile cards
export const MobileUserCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='70%' height={24} />
                <Skeleton variant='text' width='90%' height={16} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Skeleton variant='text' width='60%' height={18} />
            <Skeleton variant='text' width='40%' height={18} />
        </Box>
    </Box>
);

export default MobileUserCard;

